import { useAui } from '@assistant-ui/react';

import { evalExpr } from '#core/lib/expr/evaluator.ts';
import type { BindingResolver } from '#core/lib/expr/session/bindingResolver.ts';
import { useEvalDeps } from '#core/lib/expr/session/EvalDepsContext.ts';
import { useSession } from '#core/lib/expr/session/SessionContext.ts';
import { buildEvalContext, createSessionBindingResolver } from '#core/lib/expr/session/useEvalExpr.ts';
import type { Value } from '#core/lib/expr/value.ts';

import { recipesFromMessages } from '#plugin-chat/lib/transport/bindingProjection.ts';

/**
 * A point-in-time evaluation scope captured from the active message
 * branch: the cross-turn {@link BindingResolver} and an `evaluate`
 * bound to it.
 */
export interface ThreadScope {
  /** Resolver over the producers visible at capture time. */
  readonly bindings: BindingResolver;
  /** Evaluate an s-expression with cross-turn names scoped to `bindings`. */
  readonly evaluate: (expression: string) => Promise<Value>;
}

/**
 * Execute-time counterpart to {@link useBindingScope}. A tool's
 * `execute` runs in the agent loop, not in React render, so it cannot
 * read the part-scoped `<BindingScopeContext>`. Instead it captures the
 * scope on demand from `aui.thread().getState().messages` — the
 * authoritative active branch. The in-flight call's own part is not yet
 * on the thread when `execute` runs, so this is exactly the set of
 * producers from prior completed turns its expression may reference.
 *
 * Returns a factory: call it once at the top of `execute` to snapshot
 * the scope, then use `evaluate` for the producer expression and
 * `bindings` for anything that needs the resolver directly (e.g.
 * `validateDashboardSpec`). Both share one recipe snapshot so a
 * concurrent branch mutation cannot tear the evaluation.
 *
 * Cross-turn references re-derive purely from the transcript and
 * memoize through `session.fetch`, so a downstream consumer reuses the
 * producer's already-cached materialization rather than re-fetching.
 */
export function useThreadScopedEval(): () => ThreadScope {
  const aui = useAui();
  const session = useSession();
  const deps = useEvalDeps();

  return () => {
    const recipes = recipesFromMessages(aui.thread().getState().messages);
    const bindings = createSessionBindingResolver(recipes, session, deps);
    return {
      bindings,
      evaluate: (expression) => evalExpr(expression, buildEvalContext(session, deps, bindings), deps.ops),
    };
  };
}
